import { Component, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';

interface PageMeta {
  title: string;
  icon: string;
  subtitle: string;
  badge?: string;
}

const PAGE_META: Record<string, PageMeta> = {
  '/dashboard': {
    title: 'Platform Dashboard',
    icon: 'fa-chart-pie',
    subtitle: 'Consolidated operational and commercial metrics across all builder tenants.',
  },
  '/tenants/builders': {
    title: 'Builders Management',
    icon: 'fa-building-flag',
    subtitle: 'Onboard, approve, and manage builder tenant registrations and KYC.',
  },
  '/tenants/builders/subdomain': {
    title: 'Subdomain Configuration',
    icon: 'fa-gear',
    subtitle: 'Configure domain and protocol for the selected builder.',
  },
  '/tenants/channel-partners': {
    title: 'Channel Partners',
    icon: 'fa-handshake',
    subtitle: 'Manage channel partner registrations, RERA verification, and approvals.',
  },
  '/subscription/catalog': {
    title: 'Module Grouping',
    icon: 'fa-sitemap',
    subtitle:
      'Module groups and modules — configure group rules and Core / Optional module status in one hierarchy.',
    badge: 'Catalog',
  },
  '/subscription/module-groups': {
    title: 'Module Groups',
    icon: 'fa-cubes',
    subtitle: 'Predefined module groups — browse core modules and premium features by plan tier.',
    badge: 'Module Groups',
  },
  '/subscription/features': {
    title: 'Features Catalog',
    icon: 'fa-toggle-on',
    subtitle: 'Predefined platform features — configure status, dependent module, validity & credits.',
    badge: 'Features',
  },
  '/subscription/addons': {
    title: 'Add-Ons Catalog',
    icon: 'fa-puzzle-piece',
    subtitle: 'Optional commercial add-ons that extend plan capacity and integrations.',
    badge: 'Add-Ons',
  },
  '/subscription/plans': {
    title: 'Subscription Plans',
    icon: 'fa-crown',
    subtitle: 'Browse, filter, and manage subscription plans — click + Add Plan to create a new one.',
    badge: 'Plans',
  },
  '/financial/invoices': {
    title: 'Transactions — Invoices',
    icon: 'fa-file-invoice-dollar',
    subtitle: 'Business invoice summaries, payment recording, and GST invoice downloads.',
    badge: 'Transactions',
  },
  '/financial/payments': {
    title: 'Transactions — Payments',
    icon: 'fa-money-bill-transfer',
    subtitle: 'Payment transactions logged against platform invoices.',
    badge: 'Transactions',
  },
  '/governance': {
    title: 'Platform Governance',
    icon: 'fa-shield-halved',
    subtitle: 'Maintenance mode, impersonation sessions, and audit trail.',
    badge: 'Governance',
  },
};

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive],
  templateUrl: './app-layout.html',
})
export class AppLayoutComponent {
  readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  pageTitle = 'Platform Dashboard';
  pageIcon = 'fa-chart-pie';
  pageSubtitle = '';
  pageBadge = '';

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.syncPageMeta());
    this.syncPageMeta();
  }

  logout(): void {
    this.auth.logout();
  }

  private syncPageMeta(): void {
    const path = this.router.url.split('?')[0];
    const meta =
      PAGE_META[path] ??
      (path.includes('/tenants/builders/') && path.endsWith('/subdomain')
        ? PAGE_META['/tenants/builders/subdomain']
        : PAGE_META['/dashboard']);
    this.pageTitle = meta.title;
    this.pageIcon = meta.icon;
    this.pageSubtitle = meta.subtitle;
    this.pageBadge = meta.badge ?? '';
  }
}
